/**
 * Tracker en vivo de objetivos de la UNIDAD (fase 16.B). Overlay glanceable
 * sobre la conversación en modo plan: muestra los objetivos outcome-framed de
 * la unit y los tilda a medida que el juez en vivo (piggyback del extractor)
 * los detecta. Arranca DESPLEGADO (y se vuelve a desplegar con cada sub-unidad
 * nueva) para que el alumno vea qué tiene que producir; se pliega al tocar la
 * barra o fuera, y plegado sigue mostrando el progreso (anillos + contador).
 * Cada rep nueva avisa con un toast con su conteo ("+1 · 2 de 3"); la palabra
 * de logro ("¡Objetivo cumplido!") queda para cuando el objetivo se completa.
 * Optimista — marca y nunca desmarca; el juez del cierre decide el dominio REAL
 * (que es espaciado: una sola charla rara vez domina la unit).
 *
 * Espejo lean del `MissionTracker` de escenarios, con la identidad naranja del
 * Programa.
 */
import { useEffect, useRef, useState } from 'react';
import { PrIconCheck, PrIconChevR, PrIconTarget } from '../program/programIcons.js';
import { RepRing } from '../program/ProgramRing.js';

interface UnitObjectivesTrackerProps {
  objectives: readonly string[];
  /** Índices demostrados en vivo (optimista). */
  completed: readonly number[];
  /** Índice recién demostrado este turno → flash + toast. */
  justIdx: number | null;
  /**
   * Sube con CADA rep nueva (modo sub-unidad). Sin él, el toast sólo salía
   * cuando cambiaba `justIdx` — o sea, en la primera rep de cada objetivo: la
   * segunda y la tercera del mismo pasaban en silencio. Ausente (legacy, un
   * objetivo se tilda una vez) → dispara el cambio de `justIdx`.
   */
  justTick?: number;
  /**
   * Fase 17.B — modo SUB-UNIDAD: reps target por objetivo (anillo). Cuando está
   * presente, cada objetivo muestra un mini-anillo `count/reps` en vez de un pip
   * binario. Ausente → modo legacy (pips binarios de la unit).
   */
  reps?: readonly number[];
  /** Demos acumuladas por objetivo (acompaña a `reps`). */
  counts?: readonly number[];
  /** Título de la sub-unidad activa (es) para el header del tracker. */
  subUnitTitle?: string;
  /** Posición de la sub-unidad activa (1..N) y total, para "N de M". */
  subUnitOrder?: number;
  subUnitTotal?: number;
  /**
   * REPASO: la sub-unidad ya estaba aprobada y el alumno volvió a practicarla.
   * Los anillos arrancan en 0 a propósito — se rotula para que ese 0/N no se
   * lea como progreso perdido (el sello del Programa sigue intacto).
   */
  review?: boolean;
}

/** El toast de una rep: el objetivo, su conteo y si con ésta se completó. */
interface RepToast {
  label: string;
  count: number;
  target: number;
  complete: boolean;
}

export function UnitObjectivesTracker({
  objectives,
  completed,
  justIdx,
  justTick,
  reps,
  counts,
  subUnitTitle,
  subUnitOrder,
  subUnitTotal,
  review = false,
}: UnitObjectivesTrackerProps) {
  // Desplegado de entrada: lo primero que el alumno ve es QUÉ tiene que decir.
  const [open, setOpen] = useState(true);
  const [toast, setToast] = useState<RepToast | null>(null);
  const [pulse, setPulse] = useState(false);
  const [flashIdx, setFlashIdx] = useState<number | null>(null);
  const repsMode = Array.isArray(reps) && reps.length === objectives.length;
  const doneSet = new Set(completed);
  const done = objectives.filter((_, i) => doneSet.has(i)).length;
  const total = objectives.length;
  const repAt = (i: number): { count: number; target: number; complete: boolean } => {
    const target = Math.max(1, reps?.[i] ?? 1);
    const count = Math.min(Math.max(0, counts?.[i] ?? 0), target);
    return { count, target, complete: count >= target };
  };

  // Cerrar el panel al tocar fuera (no sólo al re-tocar la barra).
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      const target = e.target as Element;
      // La barra de «Armá tu nave» vive justo debajo: cerrar el panel la subía en medio del toque y
      // «Volver a la nave» se perdía (ronda de la noche del 19/10, #546).
      if (target.closest?.('.sp-wrap')) return;
      if (wrapRef.current && !wrapRef.current.contains(target)) setOpen(false);
    };
    // `pointerdown` en captura: cierra antes de que el click llegue a otro control.
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  // Cada sub-unidad nueva (avance o elección en el panel) se vuelve a mostrar
  // desplegada: son objetivos que el alumno todavía no leyó.
  const partKey = `${subUnitOrder ?? ''}|${subUnitTitle ?? ''}`;
  const lastPartRef = useRef(partKey);
  useEffect(() => {
    if (partKey === lastPartRef.current) return;
    lastPartRef.current = partKey;
    setOpen(true);
  }, [partKey]);

  const [toastNonce, setToastNonce] = useState(0);
  const lastJustRef = useRef<number | null>(null);
  const lastTickRef = useRef(justTick ?? 0);
  // Objetivos/conteos del render actual: llegan en el MISMO render que el tick
  // (mismo handler del evento), así que el efecto los lee de acá y sus
  // triggers siguen siendo sólo el tick y el índice.
  const latestRef = useRef({ objectives, repsMode, repAt });
  latestRef.current = { objectives, repsMode, repAt };
  useEffect(() => {
    if (justIdx == null) return;
    if (justTick !== undefined) {
      // Modo sub-unidad: una rep nueva = un tick nuevo (aunque sea el mismo objetivo).
      if (justTick === lastTickRef.current) return;
      lastTickRef.current = justTick;
    } else {
      if (justIdx === lastJustRef.current) return;
      lastJustRef.current = justIdx;
    }
    const latest = latestRef.current;
    const label = latest.objectives[justIdx];
    if (!label) return;
    // Legacy (sin reps): tildar = cumplido.
    const rep = latest.repsMode ? latest.repAt(justIdx) : { count: 1, target: 1, complete: true };
    setFlashIdx(justIdx);
    setPulse(true);
    setToast({ label, count: rep.count, target: rep.target, complete: rep.complete });
    setToastNonce((n) => n + 1);
    const t1 = setTimeout(() => setPulse(false), 650);
    const t2 = setTimeout(() => setFlashIdx(null), 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [justTick, justIdx]);

  // Auto-dismiss del toast, DESACOPLADO de `justIdx`: si `justIdx` se resetea a
  // null (p. ej. al avanzar de sub-unidad) el efecto de arriba salía temprano y
  // su cleanup cancelaba el timer → el toast quedaba colgado. Acá el timer vive
  // de `toastNonce` (sube cada vez que se muestra un toast), así siempre se va.
  useEffect(() => {
    if (toastNonce === 0) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toastNonce]);

  if (total === 0) return null;

  return (
    <div className={`ut-wrap${open ? ' open' : ''}`} ref={wrapRef}>
      <button type="button" className="ut-bar" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Objetivos">
        <span className="ut-bar-ic">
          <PrIconTarget size={14} color="#ff9d6c" />
        </span>
        <span className="ut-bar-label">{repsMode && subUnitTitle ? subUnitTitle : 'Objetivos'}</span>
        {repsMode && review && <span className="ut-tag">Repaso</span>}
        <div className="ut-pips" aria-hidden="true">
          {objectives.map((_, i) => {
            if (repsMode) {
              const r = repAt(i);
              return (
                <RepRing
                  key={i}
                  size={18}
                  done={r.count}
                  target={r.target}
                  color="#ff9d6c"
                  complete={r.complete}
                  flash={flashIdx === i}
                />
              );
            }
            return (
              <span
                key={i}
                className={`ut-pip${doneSet.has(i) ? ' done' : ''}${flashIdx === i ? ' flash' : ''}`}
              />
            );
          })}
        </div>
        <span className={`ut-count${pulse ? ' pulse' : ''}`}>
          <b>{done}</b>/{total}
        </span>
        <span className="ut-chev">
          <PrIconChevR size={15} color="var(--text-faint)" />
        </span>
      </button>

      {toast && (
        <div className="ut-toast" role="status" aria-live="polite">
          {toast.complete ? (
            <span className="ut-toast-ic">
              <PrIconCheck size={11} color="#0c0d15" />
            </span>
          ) : (
            <span className="ut-toast-ring" aria-hidden="true">
              <RepRing size={18} done={toast.count} target={toast.target} color="#ff9d6c" />
            </span>
          )}
          <span className="ut-toast-tx">
            <b>
              {toast.complete ? '¡Objetivo cumplido! ' : `+1 · ${toast.count} de ${toast.target} `}
            </b>
            {toast.label}
          </span>
        </div>
      )}

      {open && (
        <div className="ut-panel">
          <div className="ut-panel-title">
            <PrIconTarget size={13} color="#ff9d6c" />
            <span className="t">
              {repsMode && subUnitTitle
                ? subUnitTitle
                : 'Esta unidad'}
            </span>
            {repsMode && review && <span className="ut-tag">Repaso</span>}
            <span style={{ flex: 1 }} />
            <span className="sc">
              {repsMode && subUnitOrder && subUnitTotal
                ? `Sub-unidad ${subUnitOrder} de ${subUnitTotal}`
                : `${done} de ${total}`}
            </span>
          </div>
          {objectives.map((label, i) => {
            const isDone = repsMode ? repAt(i).complete : doneSet.has(i);
            return (
              <div key={i} className={`ut-item${isDone ? ' done' : ''}${flashIdx === i ? ' just' : ''}`}>
                {repsMode ? (
                  <span className="ut-check" style={{ background: 'none' }}>
                    <RepRing
                      size={20}
                      done={repAt(i).count}
                      target={repAt(i).target}
                      color="#ff9d6c"
                      complete={repAt(i).complete}
                      flash={flashIdx === i}
                    />
                  </span>
                ) : (
                  <span className="ut-check">{isDone && <PrIconCheck size={12} color="#0c0d15" />}</span>
                )}
                <span className="ut-label">{label}</span>
              </div>
            );
          })}
          <div className="ut-note">
            {repsMode && review
              ? 'Repaso: esta sub-unidad ya está aprobada y sigue así pase lo que pase acá. Volvé a producir cada objetivo, como la primera vez.'
              : repsMode
                ? 'Producí cada objetivo varias veces (cada vez distinto) para completar la sub-unidad.'
                : 'El dominio se gana practicando en varias sesiones — no de una.'}
          </div>
        </div>
      )}
    </div>
  );
}
